/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { type CustomerHome, retrieveBenefits, retrieveCustomerHome } from '@amzn/vinyl-amzn/cbl'
import type { Entitlement } from '@amzn/vinyl-dmls'
import { getAuthToken } from '@/auth/authRef'
import { getDeviceInfo } from '@/device/device'

/**
 * Entitlement and account information for the logged in customer.
 */
export type CustomerAccountInfo = {
    readonly entitlementList: ReadonlySet<Entitlement>
    readonly customerHome: CustomerHome
}

/**
 * Provides the basic account information and entitlement tier.
 */
export async function retrieveCustomerInfo(): Promise<CustomerAccountInfo> {
    const deviceInfo = getDeviceInfo()
    const oAuthToken = await getAuthToken()
    const customerHome = await retrieveCustomerHome(
        { oAuthToken },
        {
            deviceId: deviceInfo.deviceId,
            deviceType: deviceInfo.deviceType,
            responseFilters: ['TERMSOFUSE', 'LOCATION', 'HISTORY', 'BENEFITS'],
        }
    )

    const customerBenefits = await retrieveBenefits(
        { oAuthToken },
        {
            deviceId: deviceInfo.deviceId,
            deviceType: deviceInfo.deviceType,
            musicTerritory: customerHome.musicTerritory,
            responseFilters: ['PRIME_MUSIC', 'CLOUD_PLAYER', 'HAWKFIRE', 'NIGHTWING'],
        }
    )

    const vinyl = await import('@amzn/vinyl-amzn')
    return {
        customerHome,
        entitlementList: vinyl.convertBenefitsFlagsToEntitlementList(
            customerBenefits.customerBenefits
        ),
    }
}
